import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';

import {
	captureOptimisticInteropWrites,
	createCacheSnapshotRestoreForCache,
	recordOptimisticInteropWrite,
	type OptimisticInteropWriteRollback,
} from '../../optimisticInterop/optimisticInteropWriteRollback.ts';
import { getFailureContext, getSecondaryCache } from '../cacheBridgeCommon.ts';
import { reportCacheBridgeFailure } from '../cacheBridgeDiagnostics.ts';
import { getApolloRelayCacheBridgeDisabledReason } from '../cacheBridgeGate.ts';
import { syncMutationCacheBridgeSafely } from '../mutationCacheBridgeSync.ts';
import { shouldSkipCacheBridgeSync } from '../utils/shouldSkipCacheBridgeSync.ts';

import {
	type CacheWriteResult,
	type MutationCacheBridgeSyncArgs,
} from './mutationCacheBridgeTypes.ts';

const mutationObjectIds = new WeakMap<object, number>();
let nextMutationObjectId = 0;

// Mirrors an optimistic mutation payload into the secondary cache and records a
// rollback point before the write. This keeps mixed Apollo/Relay readers in sync
// during the optimistic window without changing the primary mutation behavior.
export function syncOptimisticMutationCacheBridge<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(
	args: MutationCacheBridgeSyncArgs<TData, TVariables> & { data: TData },
): OptimisticInteropWriteRollback | null {
	// Avoid cloning either cache unless the bridge would actually be allowed to
	// write. The normal mutation bridge has the same checks internally, but the
	// optimistic path must run them before capturing rollback snapshots.
	if (shouldSkipCacheBridgeSync(args)) return null;

	try {
		if (getApolloRelayCacheBridgeDisabledReason()) return null;
	} catch (error) {
		reportCacheBridgeFailure(
			error,
			getFailureContext('mutation', 'write', getSecondaryCache(args.source)),
		);
		return null;
	}

	const syncState: {
		writeResult: CacheWriteResult | null;
	} = { writeResult: null };
	const targetCache = getSecondaryCache(args.source);

	const rollback = captureOptimisticInteropWrites(() => {
		recordOptimisticInteropWrite({
			getKey: () =>
				getOptimisticMutationPayloadWriteKey({
					mutation: args.mutation,
					variables: args.variables,
					source: args.source,
					useAggClient: args.useAggClient,
				}),
			createRestore: () => createCacheSnapshotRestoreForCache(targetCache),
			applyWrite: () => {
				syncState.writeResult = syncMutationCacheBridgeSafely(args);
			},
		});

		syncState.writeResult = syncMutationCacheBridgeSafely(args);
	});

	if (syncState.writeResult?.status !== 'written' || !rollback.hasCapturedWrites()) {
		rollback.cleanup();
		return null;
	}

	return rollback;
}

// Settles the optimistic secondary write by restoring it first, applying the
// real mutation result while the cache is back at its base state, then allowing
// any later optimistic layers to reapply on top.
export function syncMutationCacheBridgeAfterOptimisticRollback(
	rollback: OptimisticInteropWriteRollback | null,
	syncFinalPayload: () => CacheWriteResult,
): CacheWriteResult {
	if (!rollback?.hasCapturedWrites()) {
		return syncFinalPayload();
	}

	let writeResult: CacheWriteResult | null = null;
	rollback.rollback({
		afterRestore: () => {
			writeResult = syncFinalPayload();
		},
	});

	return writeResult ?? { status: 'skipped', reason: 'optimistic-rollback-without-final-sync' };
}

function getOptimisticMutationPayloadWriteKey<
	TVariables extends Record<string, unknown> = OperationVariables,
>({
	mutation,
	variables,
	source,
	useAggClient,
}: {
	mutation: GraphQLTaggedNode;
	variables?: TVariables;
	source: 'apollo' | 'relay';
	useAggClient?: boolean;
}) {
	const mutationObject = mutation as object;
	if (!mutationObjectIds.has(mutationObject)) {
		mutationObjectIds.set(mutationObject, nextMutationObjectId++);
	}

	return JSON.stringify({
		mutation: mutationObjectIds.get(mutationObject),
		variables: variables ?? {},
		source,
		useAggClient: Boolean(useAggClient),
	});
}
